import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Database, Download, AlertTriangle, Loader2, Server, HardDrive, Table2 } from 'lucide-react'
import { apiGet } from '../lib/api'

interface BackupInfo {
  host: string
  database: string
  sizeBytes: number
  tableCount: number
}

function fmtBytes(n: number): string {
  if (!n || n <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = n
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`
}

export function Backup() {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState('')

  const { data, isLoading } = useQuery<BackupInfo>({
    queryKey: ['backup-info'],
    queryFn: () => apiGet('/api/admin/backup/info'),
    refetchOnWindowFocus: false,
  })

  async function onDownload() {
    setError('')
    setDownloading(true)
    try {
      // Navegamos directo al endpoint para que el browser maneje el download
      // sin tener que cargar el archivo en memoria del JS.
      window.location.href = '/api/admin/backup/download'
      // El reset del estado pasa después de un instante porque la navegación
      // no genera evento de "completado" desde JS.
      setTimeout(() => setDownloading(false), 4000)
    } catch (e) {
      setError((e as Error)?.message || 'Error iniciando backup')
      setDownloading(false)
    }
  }

  return (
    <>
      <Topbar
        title="Backup de base de datos"
        subtitle="Descarga inmediata, sin historial"
      />
      <div className="p-6">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-ink-200 bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <div className="grid size-10 place-items-center rounded-xl bg-brand-50 text-brand-600">
                  <Database className="size-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-ink-900">Base de datos actual</h2>
                  <p className="text-xs text-ink-500">
                    Conexión activa del backend
                  </p>
                </div>
              </div>

              {isLoading && (
                <div className="grid place-items-center py-12 text-ink-500">
                  <Loader2 className="size-7 animate-spin" />
                  <p className="mt-2 text-sm">Consultando metadata…</p>
                </div>
              )}

              {data && (
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-ink-100 bg-ink-50 px-4 py-3">
                    <dt className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-ink-500">
                      <Server className="size-3.5" />
                      Host
                    </dt>
                    <dd className="mt-1 font-mono text-sm text-ink-900">{data.host}</dd>
                  </div>
                  <div className="rounded-xl border border-ink-100 bg-ink-50 px-4 py-3">
                    <dt className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-ink-500">
                      <Database className="size-3.5" />
                      Base
                    </dt>
                    <dd className="mt-1 font-mono text-sm text-ink-900">{data.database}</dd>
                  </div>
                  <div className="rounded-xl border border-ink-100 bg-ink-50 px-4 py-3">
                    <dt className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-ink-500">
                      <HardDrive className="size-3.5" />
                      Tamaño aproximado
                    </dt>
                    <dd className="mt-1 text-sm font-semibold text-ink-900">
                      {fmtBytes(data.sizeBytes)}
                    </dd>
                  </div>
                  <div className="rounded-xl border border-ink-100 bg-ink-50 px-4 py-3">
                    <dt className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-ink-500">
                      <Table2 className="size-3.5" />
                      Tablas
                    </dt>
                    <dd className="mt-1 text-sm font-semibold text-ink-900">{data.tableCount}</dd>
                  </div>
                </dl>
              )}
            </div>

            <div className="rounded-2xl border border-ink-200 bg-white p-6 shadow-sm">
              <h2 className="mb-2 text-base font-semibold text-ink-900">Generar backup ahora</h2>
              <p className="mb-5 text-sm text-ink-600">
                Se descargará un archivo <code className="rounded bg-ink-100 px-1 py-0.5 font-mono text-xs">.sql.gz</code>{' '}
                con el dump completo de la base de datos. Si <code className="rounded bg-ink-100 px-1 py-0.5 font-mono text-xs">mysqldump</code>{' '}
                no está disponible en el servidor, se entrega un fallback{' '}
                <code className="rounded bg-ink-100 px-1 py-0.5 font-mono text-xs">.json.gz</code>{' '}
                con las tablas principales (orders, order_items, products, users, categories, brands).
              </p>

              <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
                <p className="text-sm text-amber-900">
                  <strong>El backup puede tardar varios minutos.</strong> No cierres esta pestaña
                  hasta que el navegador comience la descarga.
                </p>
              </div>

              <button
                type="button"
                onClick={onDownload}
                disabled={downloading}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-60"
              >
                {downloading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Generando…
                  </>
                ) : (
                  <>
                    <Download className="size-4" />
                    Descargar backup ahora
                  </>
                )}
              </button>

              {error && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-sm">
              <h3 className="mb-3 text-sm font-semibold text-ink-900">Detalles del proceso</h3>
              <ul className="space-y-3 text-sm text-ink-700">
                <li className="flex items-start gap-2">
                  <Badge variant="success">1</Badge>
                  <span>
                    El servidor ejecuta <code className="rounded bg-ink-100 px-1 font-mono text-xs">mysqldump</code> y comprime el resultado con gzip.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Badge variant="success">2</Badge>
                  <span>
                    El stream se envía progresivamente al navegador como descarga directa.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Badge variant="success">3</Badge>
                  <span>
                    Cada backup queda registrado en <code className="rounded bg-ink-100 px-1 font-mono text-xs">/logs</code> (categoría <code className="rounded bg-ink-100 px-1 font-mono text-xs">backup</code>).
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-sm">
              <h3 className="mb-2 text-sm font-semibold text-ink-900">Restauración</h3>
              <p className="text-sm text-ink-600">
                Para restaurar:{' '}
                <code className="block mt-2 break-all rounded bg-ink-900 px-3 py-2 font-mono text-xs text-ink-100">
                  gunzip -c maz-backup-…sql.gz | mysql -u user -p maz_v2ad
                </code>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Backup
